import { useState } from 'react';

import { Link } from 'react-router-dom';
import { AlertTriangle, GitBranch, Info, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Switch } from '@/ui/base/switch';

import {
  ROTULO_DO_NIVEL,
  ROTULO_DO_PROVEDOR,
  ROTULO_DO_TIPO,
  provedorAtendeNivel,
  usePoliticasDeAssinatura,
  useProvedoresAceitos,
  type NivelDeAssinatura,
  type PoliticaDeAssinatura,
  type ProvedorAceito,
} from './dados';

/**
 * `/admin/parametros/provedores-assinatura` — com qual certificado cada
 * documento pode ser assinado, e em que ordem as opções aparecem ao cidadão.
 *
 * A CONFERÊNCIA DE COMPATIBILIDADE FUNCIONA DE VERDADE; O "SALVAR" NÃO. Que
 * nível cada provedor emite não é parametrizável — decorre do que a tecnologia
 * de certificado é juridicamente (ver `NIVEIS_QUE_O_PROVEDOR_EMITE` em
 * `dados.ts`). Então a tela consegue dizer, sem API nenhuma, que ativar o
 * Assinador gov.br numa política que exige assinatura qualificada não vai
 * funcionar — e impede a ativação em vez de deixar o erro aparecer só na hora em
 * que um credenciado tentar assinar.
 *
 * Gravar precisaria de `/provedores-assinatura`, que não existe na API: a tabela
 * `provedores_assinatura_aceitos` (migração `0002_dominio_base.sql`) não tem
 * política de escrita. O botão mostra o aviso honesto de indisponibilidade
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 */
export function ProvedoresAssinaturaPage() {
  const politicas = usePoliticasDeAssinatura();
  const provedores = useProvedoresAceitos();

  const carregando = politicas.isLoading || provedores.isLoading;
  const comErro = politicas.isError || provedores.isError;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Provedores de assinatura aceitos"
        description="Define com qual certificado cada documento pode ser assinado e em que ordem o cidadão verá as opções."
      >
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/parametros/fluxo-assinatura">
            <GitBranch className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Como funciona o fluxo
          </Link>
        </Button>
      </PageHeader>

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Cada provedor emite um nível só</AlertTitle>
        <AlertDescription className="text-xs">
          Aceite eletrônico equivale a assinatura <strong>simples</strong>; o Assinador gov.br emite{' '}
          <strong>avançada</strong>; certificado ICP-Brasil, A1 ou A3, emite{' '}
          <strong>qualificada</strong>. Provedor incapaz do nível que a política exige aparece
          marcado e não pode ser ativado. O nível de cada documento fica em{' '}
          <Link to="/admin/parametros/politica-assinatura" className="font-medium underline">
            Política de assinatura digital
          </Link>
          .
        </AlertDescription>
      </Alert>

      {comErro ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar os provedores agora. Verifique sua conexão e tente novamente."
          primaryAction={{
            label: 'Tentar de novo',
            onClick: () => {
              void politicas.refetch();
              void provedores.refetch();
            },
          }}
        />
      ) : carregando ? (
        <Spinner />
      ) : !politicas.data || politicas.data.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="Nenhuma política cadastrada"
          description="Provedores são aceitos por política. Cadastre a política do documento primeiro."
        />
      ) : (
        <div className="space-y-4">
          {politicas.data.map((politica) => (
            <GrupoDaPolitica
              key={politica.id}
              politica={politica}
              provedores={(provedores.data ?? []).filter(
                (provedor) => provedor.politica_id === politica.id,
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface GrupoDaPoliticaProps {
  politica: PoliticaDeAssinatura;
  provedores: ProvedorAceito[];
}

function GrupoDaPolitica({ politica, provedores }: GrupoDaPoliticaProps) {
  const compativeisAtivos = provedores.filter(
    (provedor) => provedor.ativo && provedorAtendeNivel(provedor.provedor, politica.nivel_minimo),
  );
  const semCobertura = politica.ativo && compativeisAtivos.length === 0;

  const ordenados = [...provedores].sort((a, b) => a.prioridade - b.prioridade);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
            {ROTULO_DO_TIPO[politica.tipo_documento]}
            <Badge variant="outline" className="text-xs">
              Exige {ROTULO_DO_NIVEL[politica.nivel_minimo].toLowerCase()}
            </Badge>
            {!politica.ativo && (
              <Badge variant="outline" className="text-xs">
                Política inativa
              </Badge>
            )}
          </CardTitle>
          {semCobertura && (
            <Badge variant="destructive" className="gap-1 text-xs">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              Sem provedor compatível
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {ordenados.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum provedor cadastrado para este tipo de documento — ele não pode ser assinado.
          </p>
        ) : (
          <div className="divide-y">
            {ordenados.map((provedor) => (
              <LinhaDoProvedor
                key={provedor.id}
                provedor={provedor}
                nivelExigido={politica.nivel_minimo}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface LinhaDoProvedorProps {
  provedor: ProvedorAceito;
  nivelExigido: NivelDeAssinatura;
}

function LinhaDoProvedor({ provedor, nivelExigido }: LinhaDoProvedorProps) {
  const [prioridade, setPrioridade] = useState(String(provedor.prioridade));
  const [ativo, setAtivo] = useState(provedor.ativo);

  const compativel = provedorAtendeNivel(provedor.provedor, nivelExigido);
  const alterado = Number(prioridade) !== provedor.prioridade || ativo !== provedor.ativo;

  function salvar() {
    const numero = Number(prioridade);
    if (!Number.isInteger(numero) || numero < 1 || numero > 99) {
      toast.error('Prioridade inválida', {
        description: 'Informe um número inteiro entre 1 e 99.',
      });
      return;
    }
    // A tabela `provedores_assinatura_aceitos` não aceita escrita hoje — não há
    // rota na API nem política de escrita no banco. O aviso é honesto.
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="grid gap-3 py-3 md:grid-cols-[1fr_auto_auto_auto] md:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
          {ROTULO_DO_PROVEDOR[provedor.provedor]}
          {!compativel && (
            <Badge variant="destructive" className="text-xs">
              Incompatível
            </Badge>
          )}
        </div>
        {provedor.observacoes && (
          <p className="text-xs text-muted-foreground">{provedor.observacoes}</p>
        )}
        {!compativel && (
          <p className="mt-0.5 text-xs text-destructive-text">
            Não emite assinatura {ROTULO_DO_NIVEL[nivelExigido].toLowerCase()}.
          </p>
        )}
      </div>

      <div className="w-24 space-y-1">
        <Label htmlFor={`prioridade-${provedor.id}`} className="text-xs">
          Ordem
        </Label>
        <Input
          id={`prioridade-${provedor.id}`}
          type="number"
          min={1}
          max={99}
          value={prioridade}
          onChange={(evento) => setPrioridade(evento.target.value)}
          className="h-9"
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id={`ativo-${provedor.id}`}
          checked={ativo}
          onCheckedChange={setAtivo}
          disabled={!compativel && !ativo}
        />
        <Label htmlFor={`ativo-${provedor.id}`} className="cursor-pointer text-xs">
          {ativo ? 'Aceito' : 'Bloqueado'}
        </Label>
      </div>

      <Button size="sm" variant="outline" onClick={salvar} disabled={!alterado}>
        <Save className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        Salvar
      </Button>
    </div>
  );
}

export default ProvedoresAssinaturaPage;
